import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, Send, CheckCircle } from "lucide-react";
import { trackFormLeadConversion } from "@/lib/adsTracking";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PrivacyConsentNotice from "@/components/PrivacyConsentNotice";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const enquirySchema = z.object({
  company_name: z.string().min(2, "Company name is required"),
  contact_person: z.string().min(2, "Contact person name is required"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  industry: z.string().optional(),
  project_location: z.string().min(2, "Project location is required"),
  seats_planned: z.string().optional(),
  timeline: z.string().optional(),
  budget_range: z.string().optional(),
  design_support: z.string().optional(),
  message: z.string().optional(),
});

export default function EnquiryForm({ compact = false }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm({
    resolver: zodResolver(enquirySchema),
    defaultValues: {
      company_name: "",
      contact_person: "",
      email: "",
      phone: "",
      industry: "Not specified",
      project_location: "",
      seats_planned: "Not specified",
      timeline: "Not specified",
      budget_range: "Not specified",
      design_support: "Not specified",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, data);
      trackFormLeadConversion({ formName: "enquiry_form" });
      setIsSubmitted(true);
      toast.success("Enquiry submitted successfully! Our team will contact you soon.");
      form.reset();
    } catch (error) {
      console.error("Enquiry submission error:", error);
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-[#0F172A] mb-3">Thank You!</h3>
        <p className="text-[#64748B] mb-6">
          Your enquiry has been received. Our team will contact you within 24 hours.
        </p>
        <Button
          onClick={() => setIsSubmitted(false)}
          variant="outline"
          className="rounded-none"
          data-testid="submit-another-btn"
        >
          Submit Another Enquiry
        </Button>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={`space-y-6 ${compact ? "" : "max-w-2xl"}`}
        data-testid="enquiry-form"
      >
        <div className={`grid ${compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"} gap-6`}>
          <FormField
            control={form.control}
            name="company_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-[#334155]">Company Name *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Your company name"
                    className="h-12 rounded-none border-slate-300 focus:border-[#0F172A]"
                    data-testid="input-company-name"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contact_person"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-[#334155]">Contact Person *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Your name"
                    className="h-12 rounded-none border-slate-300 focus:border-[#0F172A]"
                    data-testid="input-contact-person"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-[#334155]">Email Address *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder="email@company.com"
                    className="h-12 rounded-none border-slate-300 focus:border-[#0F172A]"
                    data-testid="input-email"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-[#334155]">Phone Number *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="tel"
                    placeholder="9820042100"
                    className="h-12 rounded-none border-slate-300 focus:border-[#0F172A]"
                    data-testid="input-phone"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="project_location"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-[#334155]">Project Location *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="City, State"
                    className="h-12 rounded-none border-slate-300 focus:border-[#0F172A]"
                    data-testid="input-location"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-[#334155]">Additional Requirements</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Tell us more about your project requirements..."
                  className="min-h-[120px] rounded-none border-slate-300 focus:border-[#0F172A] resize-none"
                  data-testid="input-message"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <PrivacyConsentNotice className="mb-2" />

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-14 bg-[#F97316] hover:bg-orange-600 text-white rounded-none font-semibold text-base uppercase tracking-wider"
          data-testid="submit-enquiry-btn"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Submit Enquiry
            </>
          )}
        </Button>
      </form>
    </Form>
  );
}
